import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, LogIn, UserPlus, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';

const ClientAuthPage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const { login, register, isAuthed } = useClientAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "next" mémorise la page + l'offre voulue avant la connexion/inscription (ex: un
  // client clique "S'abonner" sur /suivi-pro sans compte -> on le ramène directement au
  // paiement juste après, au lieu de le laisser sur son tableau de bord.
  const next = searchParams.get('next');

  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthed) navigate(next || '/mon-espace', { replace: true });
  }, [isAuthed, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      navigate(next || '/mon-espace', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <>
      <SEOHead
        title={L('Espace client | Body-Up', 'Client area | Body-Up')}
        description={L('Connectez-vous pour retrouver vos résultats nutrition et votre historique de calculs.', 'Log in to access your nutrition results and calculation history.')}
        path="/espace-client"
        noindex
      />
      <main className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-28">
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-[26rem] h-[26rem] bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -right-24 w-[24rem] h-[24rem] bg-yellow-500/10 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative w-full max-w-md rounded-3xl border border-white/10 bg-zinc-900/70 backdrop-blur p-8"
          data-testid="client-auth-card"
        >
          <h1 className="text-3xl font-extrabold text-center">
            {mode === 'login' ? L('Bon retour', 'Welcome back') : L('Créer mon espace', 'Create my account')}
          </h1>
          <p className="text-sm text-zinc-400 text-center mt-2">
            {next
              ? L('Connectez-vous pour finaliser votre abonnement — vous serez redirigé au paiement juste après.', 'Log in to finalize your subscription — you\'ll be redirected to payment right after.')
              : L('Retrouvez vos résultats et votre historique.', 'Access your results and history.')}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-xl bg-zinc-800/60 border border-white/10">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === 'login' ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'text-zinc-400'}`}
              data-testid="tab-login"
            >
              <LogIn className="w-4 h-4 inline mr-1.5" />{L('Connexion', 'Log in')}
            </button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${mode === 'register' ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'text-zinc-400'}`}
              data-testid="tab-register"
            >
              <UserPlus className="w-4 h-4 inline mr-1.5" />{L('Inscription', 'Sign up')}
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === 'register' && (
              <Field icon={User} label={L('Prénom (optionnel)', 'First name (optional)')}>
                <input
                  type="text" value={form.name} onChange={(e) => onChange('name', e.target.value)}
                  className="auth-input" placeholder={L('Votre prénom', 'Your first name')} data-testid="input-name"
                />
              </Field>
            )}
            <Field icon={Mail} label="Email">
              <input
                type="email" required value={form.email} onChange={(e) => onChange('email', e.target.value)}
                className="auth-input" placeholder="vous@exemple.com" data-testid="input-email"
              />
            </Field>
            <Field icon={Lock} label={L('Mot de passe', 'Password')}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'} required minLength={6} value={form.password} onChange={(e) => onChange('password', e.target.value)}
                  className="auth-input pr-11" placeholder="••••••••" data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  aria-label={L('Afficher le mot de passe', 'Show password')}
                  data-testid="toggle-show-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>

            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="auth-error">{error}</p>
            )}

            <button
              type="submit" disabled={busy}
              className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform disabled:opacity-60"
              data-testid="auth-submit"
            >
              {busy ? L('Veuillez patienter…', 'Please wait…') : (mode === 'login' ? L('Se connecter', 'Log in') : L('Créer mon compte', 'Create account'))}
              {!busy && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
            </button>
            
            {/* Forgot Password Link */}
            {mode === 'login' && (
              <Link
                to="/reset-password"
                className="block text-center text-sm text-zinc-400 hover:text-primary transition-colors underline underline-offset-2"
              >
                {L('Mot de passe oublié ?', 'Forgot password?')}
              </Link>
            )}
          </form>

          <p className="mt-5 text-xs text-zinc-500 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Vos données restent confidentielles.', 'Your data stays private.')}
          </p>
          <p className="mt-4 text-center text-sm text-zinc-400">
            {L('Pas encore de calcul ?', 'No calculation yet?')}{' '}
            <Link to="/calculateur-calories" className="text-primary font-semibold hover:underline">{L('Lancer le calculateur', 'Open the calculator')}</Link>
          </p>
          <p className="mt-2 text-center text-xs text-zinc-600" data-testid="admin-hint-link">
            {L('Vous êtes coach/admin ?', 'Are you a coach/admin?')}{' '}
            <Link to="/admin" className="text-zinc-500 hover:text-primary underline underline-offset-2">{L('Connexion admin', 'Admin login')}</Link>
          </p>
        </motion.div>
      </main>
    </>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="block text-sm font-medium text-zinc-300 mb-1.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-primary" /> {label}
    </label>
    {children}
  </div>
);

export default ClientAuthPage;
